import { z } from "zod";

export const BillingModeSchema = z.enum(["test", "live"]);

export const BillingIntervalUnitSchema = z.enum(["week", "month", "year"]);

const BillingIntervalCountSchema = z.number().int().min(1).max(65535);

export const BillingOptionStripeSyncStatusSchema = z.enum(["pending", "synced", "failed"]);

export const PlanInvitationStatusSchema = z.enum([
    "pending",
    "redeemed",
    "revoked",
    "expired",
    "superseded",
]);

export const BillingProviderSubscriptionStatusSchema = z.enum([
    "checkout_pending",
    "incomplete",
    "incomplete_expired",
    "trialing",
    "active",
    "past_due",
    "unpaid",
    "paused",
    "canceled",
]);

export const BillingOptionStripeSyncSchema = z.object({
    mode: BillingModeSchema,
    status: BillingOptionStripeSyncStatusSchema,
    revision: z.number().int(),
    stripe_product_id: z.string().nullable(),
    last_error: z.string().nullable(),
    last_attempted_at: z.string().nullable(),
    synced_at: z.string().nullable(),
});

export const BillingOptionStripePriceSchema = z.object({
    id: z.number().int(),
    mode: BillingModeSchema,
    stripe_product_id: z.string(),
    stripe_price_id: z.string(),
    amount_cents: z.number().int(),
    currency: z.string(),
    interval_unit: BillingIntervalUnitSchema,
    interval_count: BillingIntervalCountSchema,
    is_current: z.boolean(),
    retired_at: z.string().nullable(),
    deactivated_in_stripe_at: z.string().nullable(),
});

// Compact shape embedded in plan list responses. `label` is the display
// name — clients present options as `{plan.name} - {label}` and the same
// combined value names the Stripe Product; recurrence stays billing data.
export const PlanBillingOptionSummarySchema = z.object({
    id: z.number().int(),
    label: z.string().min(1),
    interval_unit: BillingIntervalUnitSchema,
    interval_count: BillingIntervalCountSchema,
    amount_cents: z.number().int(),
    currency: z.string(),
    is_active: z.boolean(),
});

export const PlanBillingOptionSchema = z.object({
    id: z.number().int(),
    v2_plan_id: z.number().int(),
    label: z.string().min(1),
    interval_unit: BillingIntervalUnitSchema,
    interval_count: BillingIntervalCountSchema,
    amount_cents: z.number().int(),
    currency: z.string(),
    is_active: z.boolean(),
    sort_order: z.number().int(),
    stripe_syncs: z.array(BillingOptionStripeSyncSchema).optional(),
    stripe_prices: z.array(BillingOptionStripePriceSchema).optional(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
});

// Open recurrence contract (backend D-057): any week/month/year interval with
// a positive count. Quarterly and yearly are Phase 0 launch cases, not an
// allow-list; Stripe is the final validator at sync time.
export const CreatePlanBillingOptionRequestSchema = z.object({
    label: z.string().min(1).max(255),
    interval_unit: BillingIntervalUnitSchema,
    interval_count: BillingIntervalCountSchema,
    amount_cents: z.number().int().min(1),
    currency: z.string().length(3).optional(),
    sort_order: z.number().int().min(0).max(65535).optional(),
});

export const UpdatePlanBillingOptionRequestSchema = CreatePlanBillingOptionRequestSchema
    .partial()
    .extend({
        is_active: z.boolean().optional(),
    });

export const PlanInvitationUserSummarySchema = z.object({
    id: z.number().int(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    email: z.string(),
});

export const PlanInvitationPlanSummarySchema = z.object({
    id: z.number().int(),
    name: z.string(),
    slug: z.string(),
    expert_type: z.string().nullable(),
    is_active: z.boolean(),
});

export const PlanInvitationBillingOptionSummarySchema = z.object({
    id: z.number().int(),
    label: z.string().min(1),
    interval_unit: BillingIntervalUnitSchema,
    interval_count: BillingIntervalCountSchema,
    amount_cents: z.number().int(),
    currency: z.string(),
    is_active: z.boolean(),
});

export const PlanInvitationSchema = z.object({
    id: z.number().int(),
    expert_id: z.number().int(),
    v2_plan_id: z.number().int(),
    v2_plan_billing_option_id: z.number().int(),
    mode: BillingModeSchema,
    status: PlanInvitationStatusSchema,
    payment_url: z.string().nullable(),
    created_by_user_id: z.number().int().nullable(),
    revoked_by_user_id: z.number().int().nullable(),
    created_by: PlanInvitationUserSummarySchema.nullable().optional(),
    revoked_by: PlanInvitationUserSummarySchema.nullable().optional(),
    superseded_by_invitation_id: z.number().int().nullable(),
    plan: PlanInvitationPlanSummarySchema.nullable().optional(),
    billing_option: PlanInvitationBillingOptionSummarySchema.nullable().optional(),
    expires_at: z.string().nullable(),
    redeemed_at: z.string().nullable(),
    revoked_at: z.string().nullable(),
    superseded_at: z.string().nullable(),
    created_at: z.string().nullable(),
    updated_at: z.string().nullable(),
});

export const CreatePlanInvitationRequestSchema = z.object({
    v2_plan_id: z.number().int(),
    v2_plan_billing_option_id: z.number().int(),
    expires_at: z.string().optional(),
});

export const RedeemPlanInvitationRequestSchema = z.object({
    token: z.string().min(32).max(512),
});

export const BillingCheckoutSummarySchema = z.object({
    id: z.number().int(),
    invitation_id: z.number().int(),
    status: BillingProviderSubscriptionStatusSchema,
    mode: BillingModeSchema,
    checkout_session_id: z.string().nullable(),
    checkout_url: z.string().nullable(),
    checkout_expires_at: z.string().nullable(),
    checkout_attempt: z.number().int(),
    trial_ends_at: z.string().nullable(),
    plan: z
        .object({
            id: z.number().int(),
            name: z.string(),
            slug: z.string(),
        })
        .nullable()
        .optional(),
    billing_option: z
        .object({
            id: z.number().int(),
            label: z.string().min(1),
            amount_cents: z.number().int(),
            currency: z.string(),
            interval_unit: BillingIntervalUnitSchema,
            interval_count: BillingIntervalCountSchema,
        })
        .nullable()
        .optional(),
    created_at: z.string().nullable(),
});

export const BillingInvitationOfferPreviewSchema = z.object({
    invitation: z.object({
        id: z.number().int(),
        mode: BillingModeSchema,
        expires_at: z.string().nullable(),
    }),
    plan: z.object({
        id: z.number().int(),
        name: z.string(),
        slug: z.string(),
    }),
    billing_option: z.object({
        id: z.number().int(),
        label: z.string(),
        amount_cents: z.number().int(),
        currency: z.string(),
        interval_unit: BillingIntervalUnitSchema,
        interval_count: z.number().int(),
    }),
});

// PR 04 only consumes the recoverable Checkout slice from the broader expert
// subscription overview. Keep the rest open for the managed subscription and
// invoice contract introduced by the following flow PR.
export const ExpertPendingCheckoutResponseSchema = z
    .object({
        pending_checkout: BillingCheckoutSummarySchema.nullable(),
    })
    .passthrough();

export const ExpertBillingPlanSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    slug: z.string(),
});

export const ExpertBillingOptionTermsSchema = z.object({
    label: z.string(),
    interval_unit: BillingIntervalUnitSchema,
    interval_count: z.number().int().positive(),
    amount_cents: z.number().int().nonnegative(),
    currency: z.string(),
});

export const ExpertProviderSubscriptionSchema = z.object({
    status: BillingProviderSubscriptionStatusSchema,
    mode: BillingModeSchema,
    cancel_at_period_end: z.boolean(),
    current_period_starts_at: z.string().nullable(),
    current_period_ends_at: z.string().nullable(),
    trial_ends_at: z.string().nullable(),
    canceled_at: z.string().nullable(),
    plan: ExpertBillingPlanSchema.nullable().optional(),
    billing_option: ExpertBillingOptionTermsSchema.nullable().optional(),
});

const ExpertBillingHttpsUrlSchema = z.string().url().refine(
    (value) => new URL(value).protocol === "https:",
    { message: "Expected an HTTPS URL" },
);

export const ExpertBillingInvoiceSchema = z.object({
    id: z.number().int(),
    subscription_id: z.number().int(),
    expert_id: z.number().int(),
    v2_plan_id: z.number().int(),
    invoice_type: z.string(),
    invoice_url: ExpertBillingHttpsUrlSchema.nullable(),
    provider: z.string().nullable(),
    provider_status: z.string().nullable(),
    amount_cents: z.number().int(),
    currency: z.string(),
    term_days_override: z.number().int().positive().nullable(),
    discount_cents: z.number().int().nullable(),
    discount_note: z.string().nullable(),
    status: z.string(),
    issued_at: z.string().nullable(),
    paid_at: z.string().nullable(),
    service_period_starts_at: z.string().nullable(),
    service_period_ends_at: z.string().nullable(),
    created_at: z.string().nullable(),
    plan: ExpertBillingPlanSchema.nullable().optional(),
});

export const ExpertBillingSubscriptionSummarySchema = z.object({
    id: z.number().int(),
    expert_id: z.number().int(),
    v2_plan_id: z.number().int(),
    status: z.string(),
    starts_at: z.string().nullable(),
    ends_at: z.string().nullable(),
    renews_at: z.string().nullable(),
    cancelled_at: z.string().nullable(),
    created_at: z.string().nullable(),
    invoices_count: z.number().int().nonnegative(),
    is_managed: z.boolean(),
    provider_subscription: ExpertProviderSubscriptionSchema.nullable(),
    plan: ExpertBillingPlanSchema.nullable().optional(),
});

export const ExpertCurrentBillingSubscriptionSchema = ExpertBillingSubscriptionSummarySchema.extend({
    recent_invoices: z.array(ExpertBillingInvoiceSchema),
});

export const ExpertSubscriptionOverviewSchema = z.object({
    current_subscription: ExpertCurrentBillingSubscriptionSchema.nullable(),
    pending_checkout: BillingCheckoutSummarySchema.nullable(),
    provider_subscription: ExpertProviderSubscriptionSchema.nullable(),
    subscriptions: z.array(ExpertBillingSubscriptionSummarySchema),
});

export const ExpertBillingPortalSessionSchema = z.object({
    url: ExpertBillingHttpsUrlSchema,
});

export const BillingRedemptionErrorCodeSchema = z.enum([
    "billing_invitation_not_found",
    "billing_invitation_not_authorized",
    "billing_invitation_unavailable",
    "billing_invitation_mode_mismatch",
    "billing_invitation_plan_unavailable",
    "billing_invitation_option_unavailable",
    "billing_invitation_price_unavailable",
    "billing_invitation_expiry_too_close",
    "billing_checkout_wait_for_paid_period",
    "billing_provider_subscription_exists",
    "billing_checkout_organization_missing",
    "billing_checkout_active",
    "billing_checkout_provider_failed",
    "billing_portal_unavailable",
]);

// Error context keys (e.g. retry_at on billing_checkout_wait_for_paid_period)
// are spread at the top level of the body, next to message.
export const BillingApiErrorBodySchema = z
    .object({
        success: z.literal(false),
        message: z.string(),
        error_code: BillingRedemptionErrorCodeSchema.optional(),
        retry_at: z.string().optional(),
    })
    .passthrough();

export const parseBillingApiError = (body: unknown): BillingApiErrorBody | null => {
    const result = BillingApiErrorBodySchema.safeParse(body);
    return result.success ? result.data : null;
};

export type BillingMode = z.infer<typeof BillingModeSchema>;
export type BillingIntervalUnit = z.infer<typeof BillingIntervalUnitSchema>;
export type BillingOptionStripeSyncStatus = z.infer<typeof BillingOptionStripeSyncStatusSchema>;
export type PlanInvitationStatus = z.infer<typeof PlanInvitationStatusSchema>;
export type BillingProviderSubscriptionStatus = z.infer<typeof BillingProviderSubscriptionStatusSchema>;
export type BillingOptionStripeSync = z.infer<typeof BillingOptionStripeSyncSchema>;
export type BillingOptionStripePrice = z.infer<typeof BillingOptionStripePriceSchema>;
export type PlanBillingOptionSummary = z.infer<typeof PlanBillingOptionSummarySchema>;
export type PlanBillingOption = z.infer<typeof PlanBillingOptionSchema>;
export type CreatePlanBillingOptionRequest = z.infer<typeof CreatePlanBillingOptionRequestSchema>;
export type UpdatePlanBillingOptionRequest = z.infer<typeof UpdatePlanBillingOptionRequestSchema>;
export type PlanInvitationUserSummary = z.infer<typeof PlanInvitationUserSummarySchema>;
export type PlanInvitationPlanSummary = z.infer<typeof PlanInvitationPlanSummarySchema>;
export type PlanInvitationBillingOptionSummary = z.infer<typeof PlanInvitationBillingOptionSummarySchema>;
export type PlanInvitation = z.infer<typeof PlanInvitationSchema>;
export type CreatePlanInvitationRequest = z.infer<typeof CreatePlanInvitationRequestSchema>;
export type RedeemPlanInvitationRequest = z.infer<typeof RedeemPlanInvitationRequestSchema>;
export type BillingCheckoutSummary = z.infer<typeof BillingCheckoutSummarySchema>;
export type BillingInvitationOfferPreview = z.infer<typeof BillingInvitationOfferPreviewSchema>;
export type ExpertPendingCheckoutResponse = z.infer<typeof ExpertPendingCheckoutResponseSchema>;
export type ExpertBillingPlan = z.infer<typeof ExpertBillingPlanSchema>;
export type ExpertBillingOptionTerms = z.infer<typeof ExpertBillingOptionTermsSchema>;
export type ExpertProviderSubscription = z.infer<typeof ExpertProviderSubscriptionSchema>;
export type ExpertBillingInvoice = z.infer<typeof ExpertBillingInvoiceSchema>;
export type ExpertBillingSubscriptionSummary = z.infer<typeof ExpertBillingSubscriptionSummarySchema>;
export type ExpertCurrentBillingSubscription = z.infer<typeof ExpertCurrentBillingSubscriptionSchema>;
export type ExpertSubscriptionOverview = z.infer<typeof ExpertSubscriptionOverviewSchema>;
export type ExpertBillingPortalSession = z.infer<typeof ExpertBillingPortalSessionSchema>;
export type BillingRedemptionErrorCode = z.infer<typeof BillingRedemptionErrorCodeSchema>;
export type BillingApiErrorBody = z.infer<typeof BillingApiErrorBodySchema>;
